import React, { useRef, useEffect, useState } from "react";

const ProblemCard = ({
  mode,
  isEditing,
  problem,
  num1,
  num2,
  operator,
  showOperators,
  setNum1,
  setNum2,
  setOperator,
  setShowOperators,
  handleSave,
  buttonStyle,
  generateNewProblem,
  setIsEditing,
}) => {
  // Add state to track which number is being edited
  const [activeInput, setActiveInput] = useState(null); // 'num1', 'num2', or null
  const [showNumpad, setShowNumpad] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Check if device is iOS/mobile
  useEffect(() => {
    const checkMobileDevice = () => {
      // Check for iOS devices or small screen touch devices
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isMobileScreen =
        window.innerWidth < 768 && "ontouchstart" in window;
      setIsMobileDevice(isIOS || isMobileScreen);
    };

    checkMobileDevice();
    window.addEventListener("resize", checkMobileDevice);

    return () => {
      window.removeEventListener("resize", checkMobileDevice);
    };
  }, []);

  const handleOperatorClick = (op) => {
    setOperator(op);
    setShowOperators(false);
    // Also close the numpad when clicking an operator
    setShowNumpad(false);
    setActiveInput(null);
  };

  // Modified function to handle operator selector click
  const handleOperatorSelectorClick = () => {
    // Hide numpad if it's currently shown
    if (showNumpad) {
      setShowNumpad(false);
      setActiveInput(null);
    }
    // Toggle operators panel
    setShowOperators(!showOperators);
  };

  // Use refs to access the input elements and the card itself
  const input1Ref = useRef(null);
  const input2Ref = useRef(null);
  const cardRef = useRef(null);

  // Store the position of the active input
  const [numpadPosition, setNumpadPosition] = useState(null);

  // Function to handle outside clicks
  useEffect(() => {
    function handleClickOutside(event) {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        // Close numpad and save changes when clicking outside
        setShowNumpad(false);
        setActiveInput(null);
        handleSave();
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleSave]); // handleSave is a dependency

  // Handle numpad click for the problem inputs
  const handleNumpadPress = (value) => {
    if (!activeInput) return;

    const updateNumber = (current) => {
      let currentStr = current.toString();

      if (value === "backspace") {
        return parseFloat(currentStr.slice(0, -1) || "0");
      } else if (value === "clear") {
        return 0;
      } else {
        // If current is 0, replace it, otherwise append
        return parseFloat(currentStr === "0" ? value : currentStr + value);
      }
    };

    if (activeInput === "num1") {
      setNum1(updateNumber(num1));
    } else if (activeInput === "num2") {
      setNum2(updateNumber(num2));
    }
  };

  // Handle input focus/click based on device type
  const handleInputClick = (inputName) => (e) => {
    // Close operator menu whenever a number input is clicked
    if (showOperators) {
      setShowOperators(false);
    }

    // Calculate position first before showing the keypad
    const inputRef = inputName === "num1" ? input1Ref : input2Ref;
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const leftPos = rect.left + rect.width / 2;

      setNumpadPosition({
        inputName,
        left: leftPos,
        element: e.currentTarget,
      });
    }

    // After position is set, then we update the active input and show the numpad
    setActiveInput(inputName);

    // For mobile devices, prevent keyboard
    if (isMobileDevice) {
      e.preventDefault();
    }

    // Show numpad after a very short delay to ensure position is set
    setTimeout(() => {
      setFadeOut(false);
      setShowNumpad(true);
    }, 10);
  };

  // Handle value change directly (for desktop)
  const handleInputChange = (inputName) => (e) => {
    const value = e.target.value;

    // Allow only numbers
    if (/^\d*$/.test(value) || value === "") {
      if (inputName === "num1") {
        setNum1(value === "" ? 0 : parseInt(value, 10));
      } else if (inputName === "num2") {
        setNum2(value === "" ? 0 : parseInt(value, 10));
      }
    }
  };

  return (
    <div
      style={{
        background: "#f3e1b2",
        padding: "clamp(10px, 3vw, 20px) clamp(20px, 5vw, 40px)",
        borderRadius: "12px",
        fontSize: "clamp(20px, 6vw, 48px)",
        fontWeight: "bold",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        fontFamily: "'Inter', sans-serif",
        textAlign: "center",
        width: "fit-content",
        position: "relative",
        display: "flex",
        flexDirection: "column", // Changed to column
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        cursor: mode === "practice" ? "pointer" : "default",
        userSelect: "none", // Prevent text selection
        WebkitUserSelect: "none", // For Safari
        MozUserSelect: "none", // For Firefox
        msUserSelect: "none", // For IE/Edge
      }}
      ref={cardRef} // Attach the card ref
      onClick={(e) => {
        if (mode === "practice" && !isEditing) {
          setIsEditing(true);
          e.stopPropagation(); // Prevent immediate save
        }
      }}
    >
      {mode === "practice" && isEditing ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "15px", // Increased gap for even more padding
              position: "relative", // Added for positioning context
            }}
          >
            <input
              ref={input1Ref}
              type={isMobileDevice ? "text" : "number"}
              inputMode={isMobileDevice ? "none" : "numeric"}
              value={num1}
              onChange={handleInputChange("num1")}
              onClick={handleInputClick("num1")}
              onTouchStart={handleInputClick("num1")}
              readOnly={isMobileDevice}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                width: "150px", // Static width
                textAlign: "center", // Ensure text is centered
                fontSize: "inherit",
                fontWeight: "inherit",
                fontFamily: "inherit",
                margin: "0 8px", // Add horizontal margins
                // match the background behind the problem card
                backgroundColor: "#fff9e9",
                userSelect: "text", // Allow text selection in input fields
                caretColor: isMobileDevice ? "transparent" : "auto", // Hide cursor on mobile
                borderColor: activeInput === "num1" ? "#9CDAED" : "#ccc", // Highlight active input
                boxShadow:
                  activeInput === "num1"
                    ? "0 0 0 2px rgba(156, 218, 237, 0.3)"
                    : "none",
              }}
            />
            <span
              onClick={handleOperatorSelectorClick} // Use the new handler function
              style={{
                cursor: "pointer",
                padding: "8px 4px", // Adjusted padding
                borderRadius: "4px",
                border: "1px solid #ccc",
                width: "60px",
                textAlign: "center", // Ensure text is centered
                userSelect: "none",
                fontSize: "inherit",
                fontWeight: "inherit",
                fontFamily: "inherit",
                position: "relative", // Ensure relative positioning
                display: "inline-flex", // Use inline-flex
                alignItems: "center", // Vertically center
                justifyContent: "center", // Horizontally center
                // match the background behind the problem card
                backgroundColor: "#fff9e9",
              }}
            >
              {operator}
            </span>
            <input
              ref={input2Ref}
              type={isMobileDevice ? "text" : "number"}
              inputMode={isMobileDevice ? "none" : "numeric"}
              value={num2}
              onChange={handleInputChange("num2")}
              onClick={handleInputClick("num2")}
              onTouchStart={handleInputClick("num2")}
              readOnly={isMobileDevice}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                width: "150px", // Static width
                textAlign: "center", // Ensure text is centered
                fontSize: "inherit",
                fontWeight: "inherit",
                fontFamily: "inherit",
                margin: "0 8px", // Add horizontal margins
                // match the background behind the problem card
                backgroundColor: "#fff9e9",
                userSelect: "text", // Allow text selection in input fields
                caretColor: isMobileDevice ? "transparent" : "auto", // Hide cursor on mobile
                borderColor: activeInput === "num2" ? "#9CDAED" : "#ccc", // Highlight active input
                boxShadow:
                  activeInput === "num2"
                    ? "0 0 0 2px rgba(156, 218, 237, 0.3)"
                    : "none",
              }}
            />

            {/* Show numpad directly under the active input */}
            {showNumpad && numpadPosition && (
              <div
                style={{
                  background: "#f3e1b2",
                  padding: "15px",
                  borderRadius: "12px",
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "8px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  animation: fadeOut
                    ? "fadeOutNumpad 0.3s ease-in-out forwards"
                    : "fadeInNumpad 0.3s ease-in-out",
                  zIndex: 1000,
                  position: "absolute",
                  top: "100%", // Position below the inputs
                  left: 0,
                  transform: `translateX(${
                    numpadPosition.left -
                    cardRef.current?.getBoundingClientRect().left -
                    135
                  }px)`, // Properly position from the start
                  marginTop: "30px", // Increased from 10px to 30px to position it lower
                  width: "auto",
                  opacity: fadeOut ? 0 : 1,
                }}
              >
                {/* Number buttons 1-9 */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleNumpadPress(num.toString())}
                    style={{
                      width: "50px",
                      height: "50px",
                      fontSize: "24px",
                      fontWeight: "bold",
                      backgroundColor: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    {num}
                  </button>
                ))}

                {/* Clear button */}
                <button
                  onClick={() => handleNumpadPress("clear")}
                  style={{
                    width: "50px",
                    height: "50px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    backgroundColor: "#ff9999",
                    color: "#800000",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  C
                </button>

                {/* Zero button */}
                <button
                  onClick={() => handleNumpadPress("0")}
                  style={{
                    width: "50px",
                    height: "50px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  0
                </button>

                {/* Backspace button */}
                <button
                  onClick={() => handleNumpadPress("backspace")}
                  style={{
                    width: "50px",
                    height: "50px",
                    fontSize: "20px",
                    fontWeight: "bold",
                    backgroundColor: "#9CDAED",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ⌫
                </button>
              </div>
            )}
          </div>

          {showOperators && (
            <div
              style={{
                display: "flex",
                flexDirection: "row", // Display operators horizontally
                justifyContent: "center", // Center the operators
                zIndex: 1002, // Increased z-index to be above numpad
                marginTop: "10px", // Add some margin to separate from the inputs
                padding: "8px", // Add padding to each operators
                // Remove the background color
                backgroundColor: "transparent",
                // Remove box shadow
                boxShadow: "none",
                borderRadius: "0",
                gap: "30px", // Add gap between the circles
              }}
            >
              {["+", "-", "×", "÷"].map((op) => (
                <span
                  key={op}
                  onClick={() => handleOperatorClick(op)}
                  style={{
                    cursor: "pointer",
                    padding: "0", // Remove padding to rely on width/height
                    margin: "0", // Remove margin to rely on gap
                    textAlign: "center",
                    userSelect: "none",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    fontFamily: "inherit",
                    width: "60px", // Increased width for a larger circle
                    height: "60px", // Increased height to match width
                    display: "flex",
                    alignItems: "center", // Center vertically
                    justifyContent: "center", // Center horizontally
                    backgroundColor: "#f3e1b2",
                    borderRadius: "50%",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    backgroundColor: "#fff9e9",
                    // Remove any potential default margins from the text
                    position: "relative",
                    // Adjust the operator position slightly upward if needed
                    paddingBottom: "5px",
                  }}
                >
                  {op}
                </span>
              ))}
            </div>
          )}
          {/* Add animations for keypad */}
          <style>
            {`
              @keyframes fadeInNumpad {
                from { opacity: 0; transform: translateX(${
                  numpadPosition
                    ? numpadPosition.left -
                      cardRef.current?.getBoundingClientRect().left -
                      135
                    : 0
                }px) translateY(-10px); }
                to { opacity: 1; transform: translateX(${
                  numpadPosition
                    ? numpadPosition.left -
                      cardRef.current?.getBoundingClientRect().left -
                      135
                    : 0
                }px) translateY(0); }
              }
              @keyframes fadeOutNumpad {
                from { opacity: 1; transform: translateX(${
                  numpadPosition
                    ? numpadPosition.left -
                      cardRef.current?.getBoundingClientRect().left -
                      84
                    : 0
                }px) translateY(0); }
                to { opacity: 0; transform: translateX(${
                  numpadPosition
                    ? numpadPosition.left -
                      cardRef.current?.getBoundingClientRect().left -
                      84
                    : 0
                }px) translateY(-10px); }
              }
            `}
          </style>
        </>
      ) : (
        <div style={{ userSelect: "none", pointerEvents: "none" }}>
          {problem.display}
        </div>
      )}
    </div>
  );
};

export default ProblemCard;
